import { seededId } from '../ids';
import type { Mutation } from '../mutations';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import { comparableUnitPrice, perUnit } from './prices';
import { shoppingMutation, type ShoppingItemLike, type ShoppingResult } from './types';

export const MAX_ITEM_NAME = 80;
export const MAX_STORE_NAME = 60;
export const MAX_UNIT = 20;
export const MAX_QUANTITY = 100_000;

export type ItemError =
  | 'name_required'
  | 'name_too_long'
  | 'store_too_long'
  | 'unit_too_long'
  | 'bad_quantity'
  | 'bad_estimate';

/** The fields a user types for an item. Everything but the name is optional. */
export interface ItemInput {
  name: string;
  quantity?: number | null;
  unit?: string | null;
  estimateMinor?: number | null;
  store?: string | null;
}

export interface ItemFields {
  name: string;
  quantity: number | null;
  unit: string | null;
  estimateMinor: number | null;
  store: string | null;
}

/** Trims and collapses spaces; '' becomes null. */
export function cleanText(v: string | null | undefined): string | null {
  const t = (v ?? '').trim().replace(/\s+/g, ' ');
  return t === '' ? null : t;
}

export function cleanItem(input: ItemInput): ItemFields {
  return {
    name: cleanText(input.name) ?? '',
    quantity: input.quantity ?? null,
    unit: cleanText(input.unit),
    estimateMinor: input.estimateMinor ?? null,
    store: cleanText(input.store),
  };
}

export function validateItem(f: Partial<ItemFields>): ItemError | null {
  if (f.name !== undefined) {
    if (f.name === '') return 'name_required';
    if (f.name.length > MAX_ITEM_NAME) return 'name_too_long';
  }
  if (f.store != null && f.store.length > MAX_STORE_NAME) return 'store_too_long';
  if (f.unit != null && f.unit.length > MAX_UNIT) return 'unit_too_long';
  if (
    f.quantity != null &&
    (!Number.isFinite(f.quantity) || f.quantity <= 0 || f.quantity > MAX_QUANTITY)
  )
    return 'bad_quantity';
  if (
    f.estimateMinor != null &&
    (!Number.isInteger(f.estimateMinor) ||
      f.estimateMinor <= 0 ||
      f.estimateMinor > MAX_AMOUNT_MINOR)
  )
    return 'bad_estimate';
  return null;
}

/**
 * The estimate an item gets when the user leaves it blank (spec §6: "an estimate defaults to the
 * last price paid"). When both the last purchase and this item have a quantity in the same unit,
 * the last unit price is scaled to the new quantity (5 kg at ₱295 → 2 kg ≈ ₱118).
 */
export function defaultEstimate(
  last: { priceMinor: number; quantity: number | null; unit: string | null } | null,
  item: { quantity: number | null; unit: string | null },
): number | null {
  if (!last) return null;
  const lastUnit = perUnit(last.priceMinor, last.quantity, last.unit);
  if (lastUnit && item.quantity != null && item.quantity > 0) {
    const mine = perUnit(1, item.quantity, item.unit);
    if (mine && mine.unit === lastUnit.unit) {
      const scaled = Math.round(comparableUnitPrice(last) * mine.baseQuantity);
      return scaled > 0 ? Math.min(scaled, MAX_AMOUNT_MINOR) : null;
    }
  }
  return last.priceMinor;
}

export function buildAddItem(
  input: ItemInput,
  existing: readonly ShoppingItemLike[],
  ctx: BuildCtx,
): ShoppingResult<{ itemId: string; mutation: Mutation }, ItemError> {
  const fields = cleanItem(input);
  const error = validateItem(fields);
  if (error) return { ok: false, error };
  const open = existing.filter((i) => i.deletedAt === null && i.tripId === null);
  const position = open.reduce((max, i) => Math.max(max, i.position), 0) + 1;
  const itemId = ctx.newId();
  const mutation = shoppingMutation(ctx, 'shopping_items', 'insert', itemId, {
    ...fields,
    currency: 'PHP',
    position,
    tripId: null,
  });
  return { ok: true, itemId, mutation };
}

/** Writes only the fields that changed. */
export function buildEditItem(
  before: ShoppingItemLike,
  input: ItemInput,
  ctx: BuildCtx,
): ShoppingResult<{ mutation: Mutation | null }, ItemError> {
  const next = cleanItem(input);
  const error = validateItem(next);
  if (error) return { ok: false, error };
  const values: Record<string, unknown> = {};
  for (const k of ['name', 'quantity', 'unit', 'estimateMinor', 'store'] as const) {
    if (next[k] !== before[k]) values[k] = next[k];
  }
  if (Object.keys(values).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: shoppingMutation(ctx, 'shopping_items', 'update', before.id, values),
  };
}

export function buildRemoveItem(id: string, ctx: BuildCtx): Mutation {
  return shoppingMutation(ctx, 'shopping_items', 'delete', id);
}

/** Undo of a remove: an insert with empty values on the soft-deleted id (conventions §14). */
export function buildRestoreItem(id: string, ctx: BuildCtx): Mutation {
  return shoppingMutation(ctx, 'shopping_items', 'insert', id, {});
}

/** The open list in the user's order. */
export function openItems<T extends ShoppingItemLike>(items: readonly T[]): T[] {
  return items
    .filter((i) => i.deletedAt === null && i.tripId === null)
    .sort((a, b) => a.position - b.position);
}

export const GROCERIES_KEY = 'groceries';

/**
 * The Groceries category Shopping's expenses go under (spec §6). Seeded only when Shopping is
 * used, not part of Spending's default set; seeded id + ifAbsent so a renamed, hidden or deleted
 * Groceries is never reset.
 */
export function buildSeedGroceries(
  userId: string,
  existingKeys: ReadonlySet<string>,
  position: number,
  ctx: BuildCtx,
): Mutation[] {
  if (existingKeys.has(GROCERIES_KEY)) return [];
  return [
    shoppingMutation(
      ctx,
      'categories',
      'insert',
      seededId(userId, 'category', GROCERIES_KEY),
      { key: GROCERIES_KEY, name: 'Groceries', icon: 'cart', hidden: false, position },
      { ifAbsent: true },
    ),
  ];
}
